import { Button } from '../ui/button.tsx';
import { ArrowLeft } from 'lucide-react';

export interface ExperimentItem {
  id: string;
  experimentId: string;
  labSampleId?: string | null;
  yourSampleId?: string | null;
  analysis?: string | null;
  analysisVariation?: string | null;
  testMethod?: string | null;
  unit: string;
  sampleResult?: string | null;
  testValue: string;
  loq?: string | null;
  inSpec?: string | null;
  testResults?: string | null;
}

export interface ExperimentRecord {
  id: string;
  reference?: string | null;
  labSampleId?: string | null;
  yourSampleId?: string | null;
  sampleDescription: string;
  batchNumber: string;
  description?: string | null;
  qcType?: string | null;
  sampleInformation?: string | null;
  templateType: string;
  timeRecord: string;
  exResult: number;
  createBy: number;
  createdAt: string;
  updatedAt: string;
}

interface ExperimentDataDetailProps {
  experiment: ExperimentRecord;
  items: ExperimentItem[];
  loading: boolean;
  onBack: () => void;
}

const formatDateTime = (value: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const textOrDash = (value: string | null | undefined) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '-';
  }
  return value;
};

const mergeBnLabel = (value: string | null | undefined) => {
  const text = textOrDash(value);
  if (text === '-') return '-';
  return text.replace(/-\d+$/, '').trim();
};

const compareItemOrder = (leftId: string, rightId: string) => {
  const left = Number.parseInt(leftId, 10);
  const right = Number.parseInt(rightId, 10);

  const leftIsNumber = Number.isFinite(left);
  const rightIsNumber = Number.isFinite(right);

  if (leftIsNumber && rightIsNumber) {
    return left - right;
  }

  if (leftIsNumber) return -1;
  if (rightIsNumber) return 1;

  return leftId.localeCompare(rightId);
};

interface BNDisplayRow {
  key: string;
  reference: string;
  unit: string;
  loq: string;
  testMethod: string;
  testValue: string;
}

const mergeBnItems = (items: ExperimentItem[]): BNDisplayRow[] => {
  const grouped = new Map<
    string,
    {
      reference: string;
      unit: string;
      loq: string;
      testMethod: string;
      testValues: string[];
    }
  >();

  items.forEach((item) => {
    const reference = mergeBnLabel(item.analysis);
    const key = [reference, textOrDash(item.unit), textOrDash(item.loq), textOrDash(item.testMethod)].join('||');
    const existing = grouped.get(key);
    const testValue = textOrDash(item.testValue);

    if (existing) {
      if (testValue !== '-') {
        existing.testValues.push(testValue);
      }
      return;
    }

    grouped.set(key, {
      reference,
      unit: textOrDash(item.unit),
      loq: textOrDash(item.loq),
      testMethod: textOrDash(item.testMethod),
      testValues: testValue === '-' ? [] : [testValue],
    });
  });

  return Array.from(grouped.entries()).map(([key, entry]) => ({
    key,
    reference: entry.reference,
    unit: entry.unit,
    loq: entry.loq,
    testMethod: entry.testMethod,
    testValue: entry.testValues.length > 0 ? entry.testValues.join(', ') : '-',
  }));
};

export default function ExperimentDataDetail({ experiment, items, loading, onBack }: ExperimentDataDetailProps) {
  const isAQ = (experiment.templateType || '').toUpperCase() === 'AQ';
  const isBN = (experiment.templateType || '').toUpperCase() === 'BN';
  const orderedItems = [...items].sort((left, right) => compareItemOrder(left.id, right.id));
  const bnItems: BNDisplayRow[] = isBN ? mergeBnItems(orderedItems) : [];
  const displayCount = isBN ? bnItems.length : orderedItems.length;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b bg-gradient-to-r from-slate-50 to-white px-5 py-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">Experiment Detail</h2>
          <p className="text-sm text-slate-500">Template: {textOrDash(experiment.templateType)}</p>
          <p className="text-sm text-slate-500">Total Items: {items.length}{isBN ? ` (Displayed: ${displayCount})` : ''}</p>
        </div>
        <Button onClick={onBack} variant="outline" className="flex w-fit items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="border-b border-slate-100 px-5 py-5">
        {isAQ ? (
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-3">
            <div className="text-sm"><span className="font-medium text-slate-500">Batch Number:</span> <span className="text-slate-900">{textOrDash(experiment.batchNumber)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Sample Description:</span> <span className="text-slate-900">{textOrDash(experiment.sampleDescription)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Template Type:</span> <span className="text-slate-900">{textOrDash(experiment.templateType)}</span></div>
          </div>
        ) : isBN ? (
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-3">
            <div className="text-sm"><span className="font-medium text-slate-500">SampleNo:</span> <span className="text-slate-900">{textOrDash(experiment.reference)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Batch No.:</span> <span className="text-slate-900">{textOrDash(experiment.batchNumber)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Description:</span> <span className="text-slate-900">{textOrDash(experiment.description)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">type:</span> <span className="text-slate-900">{textOrDash(experiment.templateType)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">QC Type:</span> <span className="text-slate-900">{textOrDash(experiment.qcType)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Sample Information:</span> <span className="text-slate-900">{textOrDash(experiment.sampleInformation)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Time:</span> <span className="text-slate-900">{formatDateTime(experiment.timeRecord)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Sampe Description:</span> <span className="text-slate-900">{textOrDash(experiment.sampleDescription)}</span></div>
          </div>
        ) : (
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-3">
            <div className="text-sm"><span className="font-medium text-slate-500">Batch Number:</span> <span className="text-slate-900">{textOrDash(experiment.batchNumber)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Lab Sample ID:</span> <span className="text-slate-900">{textOrDash(experiment.labSampleId)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Your Sample ID:</span> <span className="text-slate-900">{textOrDash(experiment.yourSampleId)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Sample Description:</span> <span className="text-slate-900">{textOrDash(experiment.sampleDescription)}</span></div>
            <div className="text-sm"><span className="font-medium text-slate-500">Time Record:</span> <span className="text-slate-900">{formatDateTime(experiment.timeRecord)}</span></div>
            <div className="text-sm md:col-span-3"><span className="font-medium text-slate-500">Description:</span> <span className="text-slate-900">{textOrDash(experiment.description)}</span></div>
            <div className="text-sm md:col-span-3"><span className="font-medium text-slate-500">Sample Information:</span> <span className="text-slate-900">{textOrDash(experiment.sampleInformation)}</span></div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading experiment result items...</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No experiment result items found.</div>
        ) : (
          <table className="min-w-full table-fixed text-sm">
            <thead className="bg-slate-50 text-slate-600">
              {isAQ ? (
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Lab Sample ID</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Your Sample ID</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Analysis</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Analysis Variation</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Result</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Value</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Unit</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Inspec</th>
                </tr>
              ) : isBN ? (
                <tr>
                  <th className="w-[30%] whitespace-nowrap px-4 py-3 text-left font-medium">Reference</th>
                  <th className="w-[15%] whitespace-nowrap px-4 py-3 text-left font-medium">Units</th>
                  <th className="w-[15%] whitespace-nowrap px-4 py-3 text-left font-medium">LOQ</th>
                  <th className="w-[20%] whitespace-nowrap px-4 py-3 text-left font-medium">Method</th>
                  <th className="w-[20%] whitespace-nowrap px-4 py-3 text-left font-medium">Test Value</th>
                </tr>
              ) : (
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Analysis</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Analysis Variation</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Test Method</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Sample Result</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Test Value</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Unit</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">LOQ</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">In Spec</th>
                </tr>
              )}
            </thead>
            <tbody>
              {isAQ ? orderedItems.map((item) => (
                <tr key={`${item.experimentId}-${item.id}`} className="border-t border-slate-100 hover:bg-slate-50/70">
                  <td className="px-4 py-2">{textOrDash(item.labSampleId)}</td>
                  <td className="px-4 py-2">{textOrDash(item.yourSampleId)}</td>
                  <td className="px-4 py-2">{textOrDash(item.analysis)}</td>
                  <td className="px-4 py-2">{textOrDash(item.analysisVariation)}</td>
                  <td className="px-4 py-2">{textOrDash(item.sampleResult)}</td>
                  <td className="px-4 py-2">{textOrDash(item.testValue)}</td>
                  <td className="px-4 py-2">{textOrDash(item.unit)}</td>
                  <td className="px-4 py-2">{textOrDash(item.inSpec)}</td>
                </tr>
              )) : isBN ? bnItems.map((item) => (
                <tr key={item.key} className="border-t border-slate-100 hover:bg-slate-50/70">
                  <td className="px-4 py-2 break-words">{item.reference}</td>
                  <td className="px-4 py-2 break-words">{item.unit}</td>
                  <td className="px-4 py-2 break-words">{item.loq}</td>
                  <td className="px-4 py-2 break-words">{item.testMethod}</td>
                  <td className="px-4 py-2 break-words">{item.testValue}</td>
                </tr>
              )) : orderedItems.map((item) => (
                <tr key={`${item.experimentId}-${item.id}`} className="border-t border-slate-100 hover:bg-slate-50/70">
                  <td className="px-4 py-2">{textOrDash(item.analysis)}</td>
                  <td className="px-4 py-2">{textOrDash(item.analysisVariation)}</td>
                  <td className="px-4 py-2">{textOrDash(item.testMethod)}</td>
                  <td className="px-4 py-2">{textOrDash(item.sampleResult)}</td>
                  <td className="px-4 py-2">{textOrDash(item.testValue)}</td>
                  <td className="px-4 py-2">{textOrDash(item.unit)}</td>
                  <td className="px-4 py-2">{textOrDash(item.loq)}</td>
                  <td className="px-4 py-2">{textOrDash(item.inSpec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
